import { computed, inject } from '@angular/core';
import { withLocalStorage, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { isNotNullOrEmpty } from '@shared/lib/core/utils/data-validation.util';
import { EnabledFeatures } from '@shared/lib/stores/enabled-features';
import { DeploymentsApi } from '@web-app/app/services/api/deployments.api';

import { HierarchicalItem } from '@web-app/app/interfaces/api/hierarchical';
import { DeploymentSummary, TenantDto } from '@web-app/app/services/typed-api';
import { storagePrefix } from '../constants/storage.const';

type DeploymentState = {
  selectedTenant: TenantDto | null;
  tenantList: HierarchicalItem<TenantDto>[];
  selectedDeployment: DeploymentSummary | null;
  deploymentList: HierarchicalItem<DeploymentSummary>[];
  deploymentIsLoading: boolean;
};

const initialState: DeploymentState = {
  selectedTenant: null,
  tenantList: [],
  selectedDeployment: null,
  deploymentList: [],
  deploymentIsLoading: false,
};

export const DeploymentStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withComputed((store) => {
    return {
      selectedTenantId: computed(() => {
        return store.selectedTenant()?.id;
      }),
      selectedDeploymentId: computed(() => {
        return store.selectedDeployment()?.id;
      }),
    };
  }),
  withMethods((store, deploymentsApi = inject(DeploymentsApi)) => ({
    setTenantList(tenantList: HierarchicalItem<TenantDto>[]) {
      patchState(store, {tenantList});
      if (tenantList.length === 1) {
        this.setTenant(tenantList[0].id);
      }
    },
    setTenant(tenant?: number) {
      if (isNotNullOrEmpty(tenant)) {
        if (store.selectedTenantId() !== tenant) {
          patchState(store, {
            selectedTenant: store.tenantList()?.find((t) => t.id === tenant),
            selectedDeployment: null,
            deploymentList: [],
            deploymentIsLoading: true,
          });
          deploymentsApi.getDeploymentTree(tenant).subscribe((deployments: any) => {
            this.setDeploymentList(deployments);
          });
        }
      } else {
        patchState(store, {selectedTenant: null, selectedDeployment: null, deploymentList: []});
      }
    },
    setDeploymentList(deploymentList?: HierarchicalItem<DeploymentSummary>[]) {
      patchState(store, {deploymentList: deploymentList, deploymentIsLoading: false});
      if (deploymentList?.length === 1) {
        this.setDeployment(deploymentList[0].id);
      }
    },
    setDeployment(deployment?: number) {
      if (isNotNullOrEmpty(deployment)) {
        if (store.selectedDeploymentId() !== deployment) {
          const deploymentFromList = store.deploymentList()?.find((d) => d.id === deployment);
          if (deploymentFromList?.tenantId === store.selectedTenantId()) {
            patchState(store, {selectedDeployment: deploymentFromList});
          }
        }
      } else {
        patchState(store, {selectedDeployment: null});
      }
    },
    clear() {
      patchState(store, {selectedDeployment: null, selectedTenant: null, tenantList: [], deploymentList: []});
    },
    getFeatureList(tenantId?: number) {
      const featureList = store.tenantList()?.find((t) => t.id === tenantId)?.featuresList;
      const tenantFeature: EnabledFeatures =
        {
          scan: featureList?.some((s: string) => s?.toLowerCase() === 'scan') ?? false,
          connectorCheck: featureList?.some((s: string) => s?.toLowerCase() === 'connectorcheck') ?? false,
          history: featureList?.some((s: string) => s?.toLowerCase() === 'history') ?? false,
          aux: featureList?.some((s: string) => s?.toLowerCase() === 'aux') ?? false,
          vfl: featureList?.some((s: string) => s?.toLowerCase() === 'vfl') ?? false,
          monitoring: featureList?.some((s: string) => s?.toLowerCase() === 'monitoring') ?? false,
          sorConverter: featureList?.some((s: string) => s?.toLowerCase() === 'sorconverter') ?? false,
          realtime: featureList?.some((s: string) => s?.toLowerCase() === 'realtime') ?? false,
        };
      return tenantFeature;
    },
  })),
  withStorageSync(
    { key: `${storagePrefix}_deployment` },
    withLocalStorage()
  ),
);
